// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setPostalCodeModal from "@guestsupply/App/Handlers/XNG/PostalCodeModalHandler";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import CallAvailability from "@guestsupply/XDL/Icons/CallAvailability";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled from "styled-components";

export interface AdditionalAvailabilityInfoModel {
    productId: string;
    longMessage: string;
    messageType: number;
    warehouseName?: string;
}
interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-CHANGES
    additionalAvailabilityInfo?: AdditionalAvailabilityInfoModel;
    altDc?: string;
    altDcSelected?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // XNG-CHANGES
    isAuthenticated: getSession(state).isAuthenticated,
    isUsWebsite: getSettingsCollection(state).customSettings?.isUsWebSite,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    // XNG-CHANGES
    setPostalCodeModal,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface AvailabilityLinkProps {
    message: string;
    hasAdditionalAvailabilityInfo: boolean;
    messageType: number;
}
export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: LinkProps;
    noAdditionalAvailabilityInfoLink?: LinkProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-CHANGES
    setPostalCodeButton?: ButtonPresentationProps;
    tooltip?: TooltipPresentationProps;
    QuestionIconStyles?: IconProps;
    ToolTipWrapper?: InjectableCss;
    callAvailabilityIcon?: IconProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-CHANGES
    additionalAvailabilityInfo,
    isAuthenticated,
    setPostalCodeModal,
    altDc,
    altDcSelected,
    isUsWebsite,
    signInUrl,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    // XNG-CHANGES

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    // XNG-CHANGES GSS-656 : Hide inventory when in stock and add availability.message check to handle the case of Drop Shipment
    if (
        availability?.messageType === AvailabilityMessageType.Available &&
        isAuthenticated &&
        !additionalAvailabilityInfo
    ) {
        return null;
    }

    const AvailabilityLink = ({
        message,
        hasAdditionalAvailabilityInfo,
        messageType,
        history,
    }: AvailabilityLinkProps & HasHistory) => {
        const avaStyles = hasAdditionalAvailabilityInfo ? styles.messageText : styles.noAdditionalAvailabilityInfoLink;

        const signInClickHandler = () => {
            if (!signInUrl) {
                history.push("/MyAccount/SignIn");
            } else {
                history.push(`${signInUrl}`);
            }
        };

        const handlePostalCodeLinkClick = () => {
            if (isAuthenticated) {
                return;
            }
            if (isUsWebsite) {
                setPostalCodeModal({ isOpen: true });
            } else {
                signInClickHandler();
            }
        };
        return (
            <>
                {/* XNG-CHANGES: GUEST-2647 */}
                {/* {isAuthenticated && messageType === AvailabilityMessageType.OutOfStock && !altDcSelected && (
                    <Icon {...styles.callAvailabilityIcon} src={CallAvailability} />
                )} */}
                <Link
                    {...avaStyles}
                    data-test-selector="availabilityMessage"
                    onClick={handlePostalCodeLinkClick}
                    aria-label={message}
                >
                    {altDcSelected
                        ? siteMessage("GUEST_OutOfStock_Selected_AlternateDC")
                        : parse(message, parserOptions)}
                </Link>
            </>
        );
    };
    const AvailabilityLinkComponent = withHistory(AvailabilityLink);

    const tooltipMessage = altDcSelected
        ? "GUEST_Cart_AlternateDC_Selected_Message"
        : altDc
        ? "GUEST_OutOfStock_AlternateDC_Message"
        : availability?.messageType === 2
        ? "OutOfStock_ToolTip"
        : "";

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (
        availability?.message?.length &&
        ![
            AvailabilityMessageType.Available,
            AvailabilityMessageType.NoMessage,
            AvailabilityMessageType.LowStock,
        ].includes(availability.messageType!)
    ) {
        inventoryTextComponent = (
            <InventoryMessage color="primary" {...styles.inventoryMessage}>
                <Tooltip
                    text={
                        !additionalAvailabilityInfo?.longMessage
                            ? siteMessage(tooltipMessage)
                            : additionalAvailabilityInfo?.longMessage
                    }
                    triggerComponent={
                        <AvailabilityLinkComponent
                            message={availability!.message}
                            hasAdditionalAvailabilityInfo={true}
                            messageType={availability.messageType!}
                        />
                    }
                    {...styles.tooltip}
                />
            </InventoryMessage>
        );
    }
    // XNG-CHANGES:add this case to handel drop shipment case
    else if (availability?.messageType === AvailabilityMessageType.Available) {
        inventoryTextComponent = (
            <InventoryMessage color="primary" {...styles.inventoryMessage}>
                <Tooltip
                    text={additionalAvailabilityInfo?.longMessage}
                    triggerComponent={
                        <AvailabilityLinkComponent
                            message={availability!.message}
                            hasAdditionalAvailabilityInfo={true}
                            messageType={availability.messageType!}
                        />
                    }
                    {...styles.tooltip}
                />
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }
    if (availability?.message?.length && !isUsWebsite && !isAuthenticated) {
        inventoryTextComponent = (
            <InventoryMessage color="primary" {...styles.inventoryMessage}>
                <AvailabilityLinkComponent
                    message={availability!.message}
                    hasAdditionalAvailabilityInfo={false}
                    messageType={availability?.messageType || 0}
                />
            </InventoryMessage>
        );
    }
    // XNG-CHANGES : Adding Loading overlay, changing the structure and adding postalcode modal
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                        aria-label="View Availability by Warehouse"
                    >
                        {translate("View Availability by Warehouse")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("QTY")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
